/**
 * Mesure de temps de frame par percentiles.
 *
 * LA MOYENNE MENT, LE p99 NON (PLAN.md §7.5). Un jeu à 60 fps de moyenne avec
 * un ramasse-miettes toutes les trois secondes est perçu comme cassé, et sa
 * moyenne ne le dit pas. Le jank est un problème de QUEUE DE DISTRIBUTION :
 * ce qu'on veut voir, ce sont les frames longues et leur fréquence.
 *
 * D'où les deux compteurs bruts en plus des percentiles : le nombre de frames
 * au-dessus de 16,7 ms (une frame ratée) et au-dessus de 33 ms (deux frames
 * ratées, parfaitement visible).
 *
 * Zéro allocation : tampon circulaire et tampon de tri préalloués.
 */
export class FrameStats {
  private readonly ring: Float32Array;
  private readonly sorted: Float32Array;
  private readonly cap: number;
  private i = 0;
  private filled = 0;

  framesOver16 = 0;
  framesOver33 = 0;
  total = 0;

  constructor(capacity = 240) {
    this.cap = capacity;
    this.ring = new Float32Array(capacity);
    this.sorted = new Float32Array(capacity);
  }

  push(ms: number): void {
    this.ring[this.i] = ms;
    this.i = (this.i + 1) % this.cap;
    if (this.filled < this.cap) this.filled++;
    this.total++;
    // ⚠️ Deux `if` indépendants, jamais un `else if`. Les seuils sont
    // CUMULATIFS : une frame à 40 ms est une frame ratée ET une double frame
    // ratée. Les rendre exclusifs ferait afficher « 0 frame > 16,7 ms » à un
    // jeu qui saccade en permanence à 40 ms — le compteur le plus rassurant
    // possible sur le pire des symptômes.
    if (ms > 16.7) this.framesOver16++;
    if (ms > 33) this.framesOver33++;
  }

  /** Un p99 calculé sur 12 échantillons ne veut rien dire. Tant que le tampon
   *  n'est pas suffisamment rempli, le verdict affiché reste suspendu plutôt
   *  que faussement rassurant. */
  get filledEnough(): boolean {
    return this.filled >= this.cap * 0.75;
  }

  /** Percentile en ms. `p` ∈ [0,1]. Trie une copie du tampon : à 240 entrées
   *  c'est négligeable, et on ne l'appelle qu'à la cadence de rafraîchissement
   *  du HUD, pas à chaque frame. */
  percentile(p: number): number {
    const n = this.filled;
    if (n === 0) return 0;
    const s = this.sorted.subarray(0, n);
    s.set(this.ring.subarray(0, n));
    s.sort();
    const idx = Math.min(n - 1, Math.max(0, Math.round(p * (n - 1))));
    return s[idx]!;
  }

  reset(): void {
    this.i = 0;
    this.filled = 0;
    this.total = 0;
    this.framesOver16 = 0;
    this.framesOver33 = 0;
  }
}
